import { useState, useCallback, useMemo } from 'react';
import {
  Clock,
  FileJson,
  Inbox,
  RefreshCw,
  SkullIcon,
  Zap,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CopyButton } from '@/components/ui/copy-button';
import { DataTablePagination } from '@/components/DataTablePagination';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageHeader } from '@/components/ui/PageHeader';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import { useNotification } from '@/context/NotificationProvider';
import { classifyError, notifyMutationError } from '@/lib/errors';
import { fullDate, relativeTime } from '@/lib/datetime';
import {
  useOutboxEvents,
  useOutboxEventTypes,
  useReprocessOutboxEvent,
} from '@/hooks/useOutboxQuery';
import type { OutboxEvent, OutboxEventsParams } from '@/api/outbox';
import {
  OutboxFilterBar,
} from './components/OutboxFilterBar';
import { OutboxPayloadDialog } from './components/OutboxPayloadDialog';
import {
  auditPeriodToStart,
  type AuditPeriod,
} from './components/AuditoriaFilterBar';

/* ------------------------------------------------------------------ */
/*  Constantes                                                         */
/* ------------------------------------------------------------------ */

const AUTO_REFRESH_MS = 30_000;

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '-';
  if (seconds < 0.001) return '<1ms';
  if (seconds < 1) return `${Math.round(seconds * 1000)}ms`;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}min ${Math.round(seconds % 60)}s`;
  return `${Math.floor(seconds / 3600)}h ${Math.round((seconds % 3600) / 60)}min`;
}

function durationColor(seconds: number | null): string {
  if (seconds === null) return 'text-muted-foreground';
  if (seconds < 1) return 'text-green-600 dark:text-green-400';
  if (seconds < 10) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

/* ------------------------------------------------------------------ */
/*  Badge helpers                                                      */
/* ------------------------------------------------------------------ */

const STATUS_STYLES: Record<string, string> = {
  PENDING: 'bg-yellow-50 text-yellow-700 dark:bg-yellow-950/50 dark:text-yellow-400',
  PROCESSING: 'bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-400',
  PROCESSED: 'bg-green-50 text-green-700 dark:bg-green-950/50 dark:text-green-400',
  FAILED: 'bg-orange-50 text-orange-700 dark:bg-orange-950/50 dark:text-orange-400',
  DEAD: 'bg-red-50 text-red-700 dark:bg-red-950/50 dark:text-red-400',
};

// "Enviado" reflete a semântica publish-only do dispatcher: o evento foi
// publicado no Redis Stream pelo outbox, mas o processamento downstream
// (worker que consome o stream) pode ter falhado silenciosamente.
// O enum técnico continua PROCESSED no backend (semântica interna correta).
const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pendente',
  PROCESSING: 'Processando',
  PROCESSED: 'Enviado',
  FAILED: 'Falhou',
  DEAD: 'Dead',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`inline-flex shrink-0 items-center rounded px-1.5 py-0.5 text-xs font-semibold leading-none ${STATUS_STYLES[status] || 'bg-gray-100 text-gray-600'}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Componentes auxiliares                                              */
/* ------------------------------------------------------------------ */

function RelativeTimestamp({ iso }: { iso: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="shrink-0 cursor-help whitespace-nowrap font-mono text-xs text-muted-foreground">
          {relativeTime(iso)}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top"><span className="text-xs">{fullDate(iso)}</span></TooltipContent>
    </Tooltip>
  );
}

/* ------------------------------------------------------------------ */
/*  Tab genérica                                                       */
/* ------------------------------------------------------------------ */

interface TabContentProps {
  autoRefresh: boolean;
  fixedStatus?: string;
}

function OutboxTabContent({ autoRefresh, fixedStatus }: TabContentProps) {
  const { showSuccess, showError } = useNotification();

  const [period, setPeriod] = useState<AuditPeriod>('24h');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(fixedStatus || '__all__');
  const [eventType, setEventType] = useState('__all__');
  // Evento selecionado para visualização do payload (admin-only).
  const [payloadEvent, setPayloadEvent] = useState<OutboxEvent | null>(null);

  const { data: eventTypes } = useOutboxEventTypes();
  const reprocessMutation = useReprocessOutboxEvent();

  const params = useMemo<OutboxEventsParams>(() => {
    const p: OutboxEventsParams = {
      created_after: auditPeriodToStart(period),
      page,
      page_size: pageSize,
    };
    const effectiveStatus = fixedStatus || (status !== '__all__' ? status : undefined);
    if (effectiveStatus) p.status_filter = effectiveStatus;
    if (eventType !== '__all__') p.event_type = eventType;
    if (search.trim()) p.search = search.trim();
    return p;
  }, [period, page, pageSize, search, status, eventType, fixedStatus]);

  const { data, isLoading, error } = useOutboxEvents(
    params,
    true,
    autoRefresh ? AUTO_REFRESH_MS : false,
  );

  const resetPage = useCallback(() => setPage(1), []);

  const handleReprocess = (eventId: string) => {
    reprocessMutation.mutate(eventId, {
      onSuccess: () => showSuccess('Evento reenfileirado para processamento'),
      onError: (err) => notifyMutationError(err, showError),
    });
  };

  return (
    <div className="space-y-4 pt-4">
      <OutboxFilterBar
        search={search}
        onSearchChange={(v) => { setSearch(v); resetPage(); }}
        status={status}
        onStatusChange={(v) => { setStatus(v); resetPage(); }}
        eventType={eventType}
        onEventTypeChange={(v) => { setEventType(v); resetPage(); }}
        eventTypes={eventTypes || []}
        period={period}
        onPeriodChange={(v) => { setPeriod(v); resetPage(); }}
        hideStatus={!!fixedStatus}
      />

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{classifyError(error).message}</AlertDescription>
        </Alert>
      )}

      {isLoading && (
        <div className="flex justify-center py-12">
          <Spinner size={32} label="Carregando eventos" />
        </div>
      )}

      {!isLoading && data && data.items.length === 0 && (
        <EmptyState
          icon={<Inbox className="h-10 w-10" />}
          title="Nenhum evento encontrado"
          description="Ajuste os filtros ou o período para encontrar eventos do outbox."
        />
      )}

      {!isLoading && data && data.items.length > 0 && (
        <>
          <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Criado</TableHead>
                  <TableHead className="hidden sm:table-cell">Tipo</TableHead>
                  <TableHead className="hidden sm:table-cell">Status</TableHead>
                  <TableHead className="hidden md:table-cell">
                    <span className="inline-flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" />
                      Latência
                    </span>
                  </TableHead>
                  <TableHead className="hidden lg:table-cell">Request ID</TableHead>
                  {fixedStatus === 'DEAD' && (
                    <>
                      <TableHead className="hidden md:table-cell text-center">Retentativas</TableHead>
                      <TableHead className="hidden md:table-cell">Erro</TableHead>
                    </>
                  )}
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell>
                      <div className="flex flex-col gap-0.5">
                        <RelativeTimestamp iso={event.created_at} />
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground sm:hidden">
                          <span className="font-mono">{event.event_type}</span>
                          <StatusBadge status={event.status} />
                          {fixedStatus === 'DEAD' && event.retry_count > 0 && (
                            <span>{event.retry_count} tent.</span>
                          )}
                          {fixedStatus === 'DEAD' && event.last_error && (
                            <span className="block w-full break-words">
                              {event.last_error}
                            </span>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <Badge variant="outline" className="font-mono text-xs">{event.event_type}</Badge>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <StatusBadge status={event.status} />
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className={`cursor-help font-mono text-xs ${durationColor(event.duration_seconds)}`}>
                            {formatDuration(event.duration_seconds)}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          <div className="space-y-1 text-xs">
                            <div>Criado: {event.created_at ? fullDate(event.created_at) : '-'}</div>
                            <div>Enviado em: {event.processed_at ? fullDate(event.processed_at) : '-'}</div>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {event.correlation_id ? (
                        <span className="inline-flex items-center gap-1">
                          <span className="font-mono text-xs">{event.correlation_id}</span>
                          <CopyButton text={event.correlation_id} />
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    {fixedStatus === 'DEAD' && (
                      <>
                        <TableCell className="hidden md:table-cell text-center">
                          <Badge variant="secondary">{event.retry_count}</Badge>
                        </TableCell>
                        <TableCell
                          className="hidden md:table-cell text-xs text-muted-foreground"
                        >
                          <div className="max-h-24 w-[360px] overflow-y-auto [overflow-wrap:anywhere]">
                            {event.last_error ?? '-'}
                          </div>
                        </TableCell>
                      </>
                    )}
                    <TableCell className="text-right">
                      <div className="inline-flex items-center justify-end gap-1">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              aria-label="Ver payload"
                              onClick={() => setPayloadEvent(event)}
                            >
                              <FileJson className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            <span className="text-xs">Ver payload</span>
                          </TooltipContent>
                        </Tooltip>
                        {fixedStatus === 'DEAD' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleReprocess(event.id)}
                            disabled={reprocessMutation.isPending}
                          >
                            <RefreshCw className="mr-1 h-4 w-4" />
                            Reprocessar
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
          </Card>

          <DataTablePagination
            page={data.page}
            pageSize={data.page_size}
            total={data.total}
            pages={data.pages}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); resetPage(); }}
            pageSizeOptions={[50, 100, 200]}
          />
        </>
      )}

      <OutboxPayloadDialog
        event={payloadEvent}
        onOpenChange={(open) => { if (!open) setPayloadEvent(null); }}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Página principal                                                   */
/* ------------------------------------------------------------------ */

export default function OutboxPage() {
  const queryClient = useQueryClient();
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('todos');

  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    queryClient.invalidateQueries({ queryKey: ['admin', 'outbox'] }).then(() => {
      setIsRefreshing(false);
    });
  }, [queryClient]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Outbox - Fila de Eventos"
        subtitle="Monitore e reprocesse eventos de side effects externos."
        icon={<Inbox className="h-6 w-6" />}
        actions={
          <>
            <label className="flex cursor-pointer items-center gap-2">
              <Switch checked={autoRefresh} onCheckedChange={setAutoRefresh} />
              <span className="whitespace-nowrap text-sm text-muted-foreground">Auto (30s)</span>
            </label>
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isRefreshing}>
              <RefreshCw className={`mr-2 h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Atualizar</span>
            </Button>
          </>
        }
      />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full justify-start">
          <TabsTrigger value="todos" className="gap-1.5">
            <Inbox className="h-4 w-4" />
            <span className="hidden sm:inline">Todos</span>
          </TabsTrigger>
          <TabsTrigger value="pendentes" className="gap-1.5">
            <Clock className="h-4 w-4" />
            <span className="hidden sm:inline">Pendentes</span>
          </TabsTrigger>
          <TabsTrigger value="enviados" className="gap-1.5">
            <Zap className="h-4 w-4" />
            <span className="hidden sm:inline">Enviados</span>
          </TabsTrigger>
          <TabsTrigger value="dead" className="gap-1.5">
            <SkullIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Dead</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="todos" forceMount className="data-[state=inactive]:hidden">
          <OutboxTabContent autoRefresh={autoRefresh} />
        </TabsContent>
        <TabsContent value="pendentes" forceMount className="data-[state=inactive]:hidden">
          <OutboxTabContent autoRefresh={autoRefresh} fixedStatus="PENDING" />
        </TabsContent>
        <TabsContent value="enviados" forceMount className="data-[state=inactive]:hidden">
          {/* fixedStatus mantém o enum técnico PROCESSED (publish-only) — o label
              visível "Enviado" reflete melhor a semântica para o admin. */}
          <OutboxTabContent autoRefresh={autoRefresh} fixedStatus="PROCESSED" />
        </TabsContent>
        <TabsContent value="dead" forceMount className="data-[state=inactive]:hidden">
          <OutboxTabContent autoRefresh={autoRefresh} fixedStatus="DEAD" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
